// Conectar la tienda desde Atlas trae el catálogo — ADR-2026-10-01-conectar-desde-atlas-trae-el-catalogo.
//
// El camino que arranca en Tiendanube (`pages/tiendanube/TiendanubeConectar.tsx`) siempre
// importó todos los productos al conectar. Las puertas de adentro de Atlas (Mi marca, el
// cartel de la home, exportar desde el historial, publicar un lanzamiento, el picker de
// "Traer de Tiendanube") sólo conectaban: la tienda quedaba vinculada y el catálogo vacío.
// Esto es lo que cada una llama después de `connectTnEnVentana`.
import { supabase } from './supabase';
import { listTnProductsPendientes, type TnProduct } from './tiendaNube';
import { importarProductosDeLaTienda, traerElCatalogoDeLaTienda } from './tiendanubeOperaciones';
import { buscarProductosNuevos } from './importaciones';

/**
 * Se dispara en `window` cuando el catálogo ya salió para el import, para que la grilla
 * de productos que esté montada se refresque sola (no tiene forma de enterarse si no:
 * quien conectó es otro componente). `detail: { brandId, enviados, desde }`, con `desde`
 * = cuándo arrancó el import (ms), para que la grilla sepa qué es nuevo.
 */
export const EVENTO_CATALOGO_EN_CAMINO = 'atlas:tn-catalogo-en-camino';

/**
 * La importación masiva falló dos veces al conectar (tardó y después falló, y el reintento también): el catálogo no
 * llegó. Queda anotado para esa marca y Mi marca lo avisa cada vez que se abre, con la salida ("Buscar productos
 * nuevos"), hasta que una búsqueda de Tiendanube salga bien (review Greptile #160).
 */
export const EVENTO_CATALOGO_NO_LLEGO = 'atlas:tn-catalogo-no-llego';
export const AVISO_CATALOGO_NO_LLEGO =
  "No pudimos traer los productos de tu Tiendanube. Tocá 'Buscar productos nuevos' para reintentar.";
// Atado a UNA marca: sin marca no se anota (si no, el aviso aparecía en cualquier marca). Review Greptile #160.
const clavePendiente = (brandId: string) => `atlas:tn-catalogo-pendiente:${brandId}`;
const claveUltimoExito = (brandId: string) => `atlas:tn-catalogo-ok:${brandId}`;

export function catalogoPendiente(brandId: string | null): boolean {
  if (!brandId) return false;
  try {
    return localStorage.getItem(clavePendiente(brandId)) === '1';
  } catch {
    return false;
  }
}

/**
 * `pendiente: true` = el catálogo no llegó; `desde` = cuándo se pidió lo que falló. Si después de eso una búsqueda
 * salió bien, el fallo viejo no vuelve a poner el aviso (review Greptile #160).
 * `pendiente: false` = una búsqueda salió bien: borra el aviso y anota la hora.
 */
export function anotarCatalogoPendiente(brandId: string | null, pendiente: boolean, desde = Date.now()): void {
  if (!brandId) return;
  try {
    if (pendiente) {
      const exito = Number(localStorage.getItem(claveUltimoExito(brandId)) ?? 0);
      if (exito >= desde) return;
      localStorage.setItem(clavePendiente(brandId), '1');
    } else {
      localStorage.removeItem(clavePendiente(brandId));
      localStorage.setItem(claveUltimoExito(brandId), String(Date.now()));
    }
  } catch {
    /* sin almacenamiento: queda el botón de siempre */
  }
}

/** Lo que tarda el worker en arrancar a crear productos — mismo margen que el resto de los imports. */
const MARGEN_DEL_WORKER_MS = 3500;

/** La marca que recibe los productos cuando quien conectó no dijo cuál: la default (la misma que resuelve `tn-products`). */
async function marcaDestino(companyId: string | null, brandId: string | null): Promise<string | null> {
  if (brandId || !companyId) return brandId;
  const { data } = await supabase
    .from('brand_profiles')
    .select('id')
    .eq('id_company', companyId)
    .eq('is_default', true)
    .eq('is_draft', false)
    .limit(1)
    .maybeSingle();
  return (data as { id?: string } | null)?.id ?? null;
}

/**
 * El catálogo entra por la importación masiva (ADR-2026-10-05-integraciones-cierre, fase 2): sin tope de
 * productos, con aviso de repetidos, y lo que la persona borró no vuelve. true = el backend la tomó, ya había una en
 * curso, o tarda en contestar. false = la rechazó o no se pudo pedir (un backend sin la fase 2, la red): quien llama
 * sigue con el import de siempre, así conectar nunca termina con el catálogo vacío.
 *
 * Si tarda más que `esperaMs` NO se arranca el import de siempre (review Greptile #160, tres vueltas): el pedido sigue
 * en camino y probablemente lo tomó, y el de siempre trae de vuelta lo que la persona borró, que la masiva respeta. Se
 * sigue esperando la respuesta en segundo plano: si al final la rechaza, se pide la masiva UNA vez más. Si tampoco,
 * Mi marca sigue ofreciendo "Buscar productos nuevos".
 */
export async function traerPorLaImportacionMasiva(
  brandId: string | null,
  esperaMs = 12_000,
  reintentoMs = 3_000,
): Promise<boolean> {
  let vencio: ReturnType<typeof setTimeout> | undefined;
  const tarda = new Promise<'tarda'>((resolver) => {
    vencio = setTimeout(() => resolver('tarda'), esperaMs);
  });
  const pedidoDesde = Date.now();
  const pedido = buscarProductosNuevos(brandId, 'tiendanube').then(() => 'tomada' as const);
  try {
    const r = await Promise.race([pedido, tarda]);
    if (r === 'tarda') {
      console.warn('[tiendanube] la importación masiva tarda en contestar; se sigue esperando');
      pedido.catch(async (e) => {
        console.warn('[tiendanube] la importación masiva falló tarde; se pide una vez más', e);
        await new Promise((r) => setTimeout(r, reintentoMs));
        await buscarProductosNuevos(brandId, 'tiendanube').catch((e2) => {
          console.warn('[tiendanube] la importación masiva volvió a fallar; queda "Buscar productos nuevos"', e2);
          anotarCatalogoPendiente(brandId, true, pedidoDesde);
          if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(EVENTO_CATALOGO_NO_LLEGO, { detail: { brandId } }));
        });
      });
    }
    return true;
  } catch (e) {
    console.warn('[tiendanube] la importación masiva no tomó el pedido; se usa el import de siempre', e);
    return false;
  } finally {
    clearTimeout(vencio);
  }
}

export interface CatalogoAlConectar {
  /** Los productos de la tienda que faltaban en Atlas. */
  encontrados: number;
  /** Los que el backend aceptó importar. */
  enviados: number;
  /** `true` = quedaron productos sin mandar (el backend rechazó una tanda o algo tiró). */
  fallo: boolean;
  /** Los que no salieron, para reintentar sólo esos (vacía si tiró antes de saberlo). */
  sinMandar: TnProduct[];
  /** Lo tomó la importación masiva: los productos van apareciendo y Mi marca muestra el avance. */
  masiva?: boolean;
}

/**
 * Importa todo lo que la tienda de esa marca tiene y Atlas todavía no. Llamarla SÓLO
 * después de que la persona conectó (lee la API de Tiendanube: nunca de fondo).
 *
 * `yaLeidos`: la lista que quien llama ya trajo de `tn-products` (el picker la tiene),
 * para no leer el catálogo dos veces. Vacía o ausente = se lee acá, con reintentos.
 *
 * Best-effort y sin `throw`: la conexión ya salió bien y es lo que la pantalla cuenta;
 * si el import falla, el catálogo se puede traer después desde "Traer de Tiendanube" o
 * el chat. Las pantallas que no muestran el resultado la llaman con `void` y siguen.
 */
export async function traerCatalogoAlConectar(
  brandId: string | null,
  yaLeidos?: TnProduct[],
): Promise<CatalogoAlConectar> {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return { encontrados: 0, enviados: 0, fallo: true, sinMandar: [] };
    const { data: profile } = await supabase
      .from('profiles')
      .select('id_company')
      .eq('id', user.id)
      .maybeSingle();
    const companyId = (profile as { id_company?: string | null } | null)?.id_company ?? null;
    const destino = await marcaDestino(companyId, brandId);

    const desde = Date.now();
    // El import de siempre: sólo si la masiva la rechaza enseguida (lo de hoy en producción).
    const deSiempre = () => {
      let primera = yaLeidos && yaLeidos.length > 0 ? yaLeidos : null;
      return traerElCatalogoDeLaTienda({
        pendientes: async () => {
          if (primera) {
            const productos = primera;
            primera = null;
            return { conectada: true, productos };
          }
          const r = await listTnProductsPendientes(destino);
          return { conectada: r.connected, productos: r.productos };
        },
        importar: (productos) =>
          importarProductosDeLaTienda(productos, { userId: user.id, companyId, brandId: destino }),
      });
    };
    if (await traerPorLaImportacionMasiva(destino)) {
      window.dispatchEvent(
        new CustomEvent(EVENTO_CATALOGO_EN_CAMINO, { detail: { brandId: destino, enviados: 0, desde } }),
      );
      const cuantos = yaLeidos?.length ?? 0;
      return { encontrados: cuantos, enviados: cuantos, fallo: false, sinMandar: [], masiva: true };
    }
    const { encontrados, enviados, sinMandar } = await deSiempre();
    if (enviados > 0) {
      await new Promise((r) => setTimeout(r, MARGEN_DEL_WORKER_MS));
      window.dispatchEvent(
        new CustomEvent(EVENTO_CATALOGO_EN_CAMINO, { detail: { brandId: destino, enviados, desde } }),
      );
    }
    return { encontrados, enviados, fallo: enviados < encontrados, sinMandar };
  } catch (e) {
    console.warn('[tiendanube] no se pudo traer el catálogo al conectar', e);
    return { encontrados: 0, enviados: 0, fallo: true, sinMandar: [] };
  }
}
